import { useEffect, useState } from "react";

/**
 * Reactive media query hook that returns the truthy value of the media query.
 *
 * @param {string} query
 * @param {boolean} initialValue
 * @returns {boolean} Boolean value of the query.
 */
export function useMediaQuery(query, initialValue = false) {
  const [matches, setMatches] = useState(initialValue);

  // Set initial matches on mount without dependencies that can trigger infinite updates.
  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches); // Set the initial value immediately.
    const handler = (event) => setMatches(event.matches); // Listen for changes.

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handler);
    } else {
      mediaQuery.addListener(handler); // For older browsers.
    }

    // Cleanup function to remove event listeners.
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handler);
      } else {
        mediaQuery.removeListener(handler);
      }
    };
  }, [query]); // Ensure `query` does not change every render.

  return matches;
}
